import React, { FC, useEffect, useState } from 'react';
import { LeafletMap } from 'src/components/Map/LeafletMap';
import { NewPolygonForm } from 'src/components/Forms/NewPolygonForm';
import { useAppDispatch, useAppSelector } from 'src/hooks/useRedux';
import {
  fetchDrawPolygons,
  fetchFavoritePolygons,
  fetchInfoData,
  fetchSelectedPolygon,
} from 'src/store/polygons/actions';
import { mapDefsObserver, pieChartObserver } from 'src/utils/observers';
import { MapButtons } from 'src/components/MapButtons';
import { MapDropBlocks } from 'src/components/MapDropBlocks';
import { Screen } from 'src/components/Screen/Index';
import { useAuth } from 'src/hooks/useAuth';
import { isEmpty } from 'src/utils';
import { mapData } from 'src/config/data';
import { MapContainer } from 'react-leaflet';

import { ReactComponent as ScreenIcon } from 'src/assets/icons/screen.svg';
import cn from 'classnames';

export const MapPage: FC = () => {
  const { isMobileButtonsOpened } = useAppSelector(state => state.map);
  const { isLoadingSelectedPolygon } = useAppSelector(state => state.polygons);
  const { statuses, blockStatuses, isLoadingAllInfo } = useAppSelector(state => state.categories);
  const { selectedLayers } = useAppSelector(state => state.map);
  const { isClustersActive } = useAppSelector(state => state.common);

  const {
    reestrPolygons,
    drawPolygons,
    isLoadingDrawPolygons,
    infoData,
    isInfoDataLoading,
    favoritePolygons,
    isLoadingFavoritePolygons,
  } = useAppSelector(state => state.polygons);

  const dispatch = useAppDispatch();
  const { user } = useAuth();

  const [lastClicked, setLastClicked] = useState<number>(3);
  const [scrIcon, setScrIcon] = useState<boolean>(true);
  const [screenPage, setScreenPage] = useState<boolean>(false);

  useEffect(() => {
    if (!user) return;

    if (!drawPolygons.length && !isLoadingDrawPolygons) {
      dispatch(fetchDrawPolygons());
    }

    if (isEmpty(infoData) && !isInfoDataLoading) {
      dispatch(fetchInfoData([]));
    }

    if (
      !favoritePolygons?.length &&
      !isLoadingFavoritePolygons &&
      (user?.isLeader || user?.isManager)
    ) {
      dispatch(fetchFavoritePolygons());
    }
  }, [user]);

  useEffect(() => {
    if (isLoadingAllInfo || !user || isLoadingSelectedPolygon) return;
    const urlSearchParams = new URLSearchParams(window.location.search);
    const cn = urlSearchParams.get('cn');

    if (!cn) return;

    dispatch(fetchSelectedPolygon({ cn, selectedLayers }));
  }, [isLoadingAllInfo, user]);

  useEffect(() => {
    mapDefsObserver([...statuses, ...blockStatuses]);
  }, [statuses, blockStatuses, reestrPolygons]);

  useEffect(() => {
    pieChartObserver();
  }, [isClustersActive]);

  return (
    <div className="w-full h-full">
      <div className="w-full h-full relative t-sm:rounded-t-md overflow-hidden">
        <div className="w-full h-full center" id="map-svg">
          <div id="screen" className="w-full h-full">
            {screenPage ? <Screen setScrIcon={setScrIcon} /> : null}

            <MapContainer className="my-map my-edit-control" {...mapData}>
              <LeafletMap />
            </MapContainer>
          </div>

          {(window.innerWidth < 1024 && isMobileButtonsOpened) ||
          (window.innerWidth > 1024 && scrIcon) ? (
            <ScreenIcon
              className={'pointer'}
              id={'screenIcon'}
              onClick={() => setScreenPage(!screenPage)}
            />
          ) : (
            <></>
          )}
        </div>

        <MapButtons />

        <MapDropBlocks lastClicked={lastClicked} setLastClicked={setLastClicked} />

        <NewPolygonForm
          onClick={() => setLastClicked(3)}
          classNames={cn({ 'z-2100': lastClicked !== 3, 'z-2200': lastClicked === 3 })}
        />
      </div>
    </div>
  );
};
